import React, { useState, useEffect, useRef } from "react";
import { CircularProgress, Box } from "@mui/material";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";

import { ApiURLs } from "../../constant/ApiUrls";
import localStorageServices from "../../services/LocalStorage";
import { LocalStorageKeys } from "../../constant/LocalStorageKeys";
import { NavigationRoutes } from "../../constant/Routes";

import {
  addArticleRequest,
  getArticle,
  getArticleCategories,
} from "./redux/articleDispatcher";

/**
 * 🔥 Titles based on category slug
 */
const titlesMap = {
  buying: ["Start Buying", "Motors", "Farming", "Animals", "Holidays & Tickets", "Electronics", "Sports & Hobbies", "Clothes & Lifestyle", "Christmas"],
  selling: ["Start Selling", "Manage Your Adverts", "How-Tos", "Pets and animals", "Troubleshooting"],
};

const AddArticle = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const editorRef = useRef(null);

  const [titleOptions, setTitleOptions] = useState([]);
  const [selectedTitle, setSelectedTitle] = useState(null);

  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("<br></br><br></br>");

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categories, setCategories] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [data, setData] = useState(null);


  /**
   * HANDLE CATEGORY CHANGE → UPDATE TITLES
   */
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);

    const slug = category?.slug;
    const titles = titlesMap[slug] || [];

    const formattedTitles = titles.map((t) => ({
      label: t,
      value: t,
    }));

    setTitleOptions(formattedTitles);
    setSelectedTitle(null);
  };


  useEffect(() => {
    const init = async () => {
      setInitialLoading(true);

      try {
        // ✅ ALWAYS fetch categories
        const catRes = await getArticleCategories();

        const categoryOptions = catRes.map((item) => ({
          label: item.name,
          value: item.id,
          slug: item.slug,
        }));

        setCategories(categoryOptions);

        // ✅ IF EDIT MODE
        if (id) {
          const articleRes = await getArticle(id);

          console.log("article:", articleRes);

          if (articleRes) {
            setData(articleRes);

            setLabel(articleRes.label || "");
            setDescription(articleRes.description || "");

            // ✅ find category
            const selectedCat = categoryOptions.find(
              (c) =>
                c.slug === articleRes.category_slug
            );

            console.log("selectedCat:", selectedCat);

            if (selectedCat) {
              setSelectedCategory(selectedCat);

              // ✅ titles based on slug
              const titles = titlesMap[selectedCat.slug] || [];

              const formattedTitles = titles.map((t) => ({
                label: t,
                value: t,
              }));

              setTitleOptions(formattedTitles);

              // ✅ match title
              const selectedT = formattedTitles.find(
                (t) =>
                  t.value.trim() ===
                  articleRes.category_title?.trim()
              );

              if (selectedT) {
                setSelectedTitle(selectedT);
              } else if (formattedTitles.length > 0) {
                setSelectedTitle(formattedTitles[0]); // fallback
              }
            }
          }
        }
      } catch (err) {
        console.log("Error init:", err);
      } finally {
        setInitialLoading(false);
      }
    };

    init();
  }, [id]);
  /**
   * SUBMIT
   */
  const handleSubmit = async () => {
    if (!selectedCategory) return alert("Category required");
    if (!selectedTitle) return alert("Title required");
    if (!label.trim()) return alert("Label required");
    if (!description || description === "<br></br><br></br>")
      return alert("Description required");

    setIsLoading(true);

    const payload = {
      article_category_id: selectedCategory.value,
      article_category_slug: selectedCategory.slug,
      category_title: selectedTitle.value,
      label,
      description,
      ...(data && { id: data.id }),
    };

    await dispatch(addArticleRequest(payload));

    // toast.success(data ? "Article updated" : "Article created");

    navigate(NavigationRoutes.ADMIN_DASHBOARD + NavigationRoutes.ARTICLE);

    // reset
    setSelectedCategory(null);
    setSelectedTitle(null);
    setTitleOptions([]);
    setLabel("");
    setDescription("");
    setIsLoading(false);
  };

  /**
   * LOADER
   */
  if (initialLoading) {
    return (
      <Box sx={styles.loaderContainer}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <div style={{ padding: "20px" }}>
      {/* CATEGORY */}
      <label style={styles.label}>Category</label>
      <Select
        options={categories}
        value={selectedCategory}
        onChange={handleCategoryChange}
        placeholder="Select category..."
      />

      <br />

      {/* TITLE DROPDOWN */}
      <label style={styles.label}>Title</label>
      <Select
        options={titleOptions}
        value={selectedTitle}
        onChange={setSelectedTitle}
        placeholder="Select title..."
      />

      <br />

      {/* LABEL */}
      <label style={styles.label}>Label</label>
      <input
        type="text"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        style={styles.input}
      />

      <br />

      {/* DESCRIPTION */}
      <label style={styles.label}>Description</label>
      <CKEditor
        editor={ClassicEditor}
        data={description}
        config={{
          simpleUpload: {
            uploadUrl: ApiURLs.UPLOAD_IMAGE,
            headers: {
              Authorization: `Bearer ${localStorageServices.getItem(
                LocalStorageKeys.userToken
              )}`,
            },
          },
        }}
        onChange={(event, editor) => {
          setDescription(editor.getData());
        }}
        onReady={(editor) => {
          editorRef.current = editor;
        }}
      />

      <br />

      {/* BUTTON */}
      <div style={{ textAlign: "center" }}>
        <button
          onClick={handleSubmit}
          style={styles.button}
          disabled={isLoading}
        >
          {isLoading ? (
            <CircularProgress size={20} style={{ color: "#fff" }} />
          ) : id ? (
            "Update Article"
          ) : (
            "Add Article"
          )}
        </button>
      </div>
    </div>
  );
};

const styles = {
  label: { fontWeight: "bold" },
  input: {
    width: "100%",
    padding: "10px",
    borderRadius: "6px",
    border: "1px solid #ccc",
  },
  button: {
    padding: "12px 20px",
    backgroundColor: "#007bff",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
  loaderContainer: {
    position: "fixed",
    top: 0,
    left: 0,
    height: "100vh",
    width: "100vw",
    backgroundColor: "rgba(255,255,255,0.8)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
};

export default AddArticle;